import { useRef, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useContent } from "../context/ContentContext";
import { apiError } from "../lib/api";

// Inline-editable text. Admins can click to edit in place; everyone else sees the text.
export default function Editable({ id, as: Tag = "span", children, className = "", multiline = false }) {
  const { user } = useAuth();
  const { get, save } = useContent();
  const isAdmin = user && user.role === "admin";
  const fallback = typeof children === "string" ? children : "";
  const value = get(id, fallback);
  const ref = useRef(null);
  const [editing, setEditing] = useState(false);

  const commit = async () => {
    setEditing(false);
    const text = ref.current ? ref.current.innerText.trim() : value;
    if (text === value) return;
    try { await save(id, text); toast.success("Saved"); }
    catch (e) { toast.error(apiError(e)); if (ref.current) ref.current.innerText = value; }
  };

  const onKey = (e) => {
    if (!multiline && e.key === "Enter") { e.preventDefault(); ref.current.blur(); }
    if (e.key === "Escape") { ref.current.innerText = value; ref.current.blur(); }
  };

  if (!isAdmin) return <Tag className={className}>{value}</Tag>;

  return (
    <Tag
      ref={ref}
      className={`${className} outline-none rounded-md transition-shadow ${editing ? "ring-2 ring-nead-pink" : "hover:ring-2 hover:ring-nead-pink/40 cursor-text"}`}
      contentEditable
      suppressContentEditableWarning
      title="Click to edit"
      data-testid={`editable-${id}`}
      onFocus={() => setEditing(true)}
      onBlur={commit}
      onKeyDown={onKey}
    >
      {value}
    </Tag>
  );
}
